/**
 * DataPointDefinition (Sprint 4.3.3, PROD-652).
 *
 * Domain shape for the design-time data-point catalogue per the Awell
 * orchestration domain model:
 *
 *   "Data point definitions describes and qualify each and every atomic
 *    piece of data that can be captured in the lifecycle of a care flow."
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/
 *   api-reference/overview/domain-model.md
 *   03-knowledge-base/domains/clinical/pathway-engineering/
 *   data-point-definition-vs-data-point.md
 *
 * The Plan v9 §5.3.3 spec maps the abstract category names to a closed
 * set used inside Care Pathway Studio:
 *   - lab              FHIR Observation (LOINC)
 *   - vital            FHIR Observation
 *   - demographic      FHIR Patient
 *   - computed         FHIR Observation with method = calculation
 *   - patient-reported FHIR QuestionnaireResponse / Observation
 *   - derived          FHIR Observation linked via Observation.derivedFrom
 *
 * Forms + calculations reference DPDs by `id` (link surface). The
 * Sprint 4.3.3 panel renders the catalogue and surfaces a click-into
 * detail view; future tickets will wire the link surface into form
 * authoring + calculation authoring.
 *
 * HC5: zero em / en dashes anywhere.
 */

/** Closed category vocabulary surfaced in the panel filter. */
export const DATA_POINT_CATEGORIES = [
  'lab',
  'vital',
  'demographic',
  'computed',
  'patient-reported',
  'derived',
] as const;

export type DataPointCategory = (typeof DATA_POINT_CATEGORIES)[number];

/** Closed value-type vocabulary per the Awell domain-model. */
export const DATA_POINT_VALUE_TYPES = [
  'boolean',
  'date',
  'number',
  'string',
  'numbers_array',
] as const;

export type DataPointValueType = (typeof DATA_POINT_VALUE_TYPES)[number];

/** Optional `(value, label)` pair for constrained possible-values. */
export interface DataPointPossibleValue {
  value: string;
  label: string;
}

/** Optional numeric range bound. */
export interface DataPointRange {
  min: number;
  max: number;
}

export interface DataPointDefinition {
  /** Stable id; forms + calculations reference this string. */
  id: string;
  /** Human + machine friendly identifier (dot-notation, e.g. `bmi.weight`). */
  key: string;
  /** Closed category. */
  category: DataPointCategory;
  /** Closed value type. */
  value_type: DataPointValueType;
  /** Display title for the panel + detail view. */
  title: string;
  /**
   * Constrained set of permitted (value, label) pairs, when relevant.
   * Only meaningful for `string` / `numbers_array` value types where the
   * pathway runtime narrows the input.
   */
  possible_values?: DataPointPossibleValue[];
  /** Numeric range bounds, when relevant. */
  range?: DataPointRange;
  /** Optional unit display string (e.g. `kg`, `mmHg`, `%`). */
  unit?: string;
  /**
   * FHIR ResourceType binding for the runtime data-point instance. Not
   * surfaced in the catalogue chrome but available for downstream
   * mappers that materialise an `Observation` / `QuestionnaireResponse`
   * from the definition.
   */
  fhir_primary?: 'Observation' | 'QuestionnaireResponse' | 'Patient';
  /** Optional LOINC code for clinical bindings. */
  loinc_code?: string;
}
